import { FC } from 'react';
import classNames from 'classnames';
import { PlainLink } from 'components/link';
import type {
  InventoryAutomationStatus,
  TriggerSource,
  UnitOutcome,
} from './types';
import styles from './page.module.scss';

const EM_DASH = '—';

const OUTCOME_LABELS: Record<string, string> = {
  completed: 'Rebuilt',
  skipped: 'Skipped',
  failed: 'Failed',
  // Three ways to have no answer, told apart by the run's own state. A pending run has not started,
  // a running one has not reached this unit, and a settled one never will. Collapsing them showed a
  // skipped run as still working, and a queued run as work already underway.
  queued: 'Queued',
  in_progress: 'In progress',
  not_run: 'Not run',
};

// A run's per-unit answer. The row itself has one state because a batch can only have one; this is
// where the batch comes apart.
const OutcomePill: FC<{ outcome: UnitOutcome }> = ({ outcome }) => {
  const label = OUTCOME_LABELS[outcome] ?? outcome;
  const tone =
    outcome === 'completed'
      ? styles.outcomeCompleted
      : outcome === 'failed'
        ? styles.outcomeFailed
        : outcome === 'skipped'
          ? styles.outcomeSkipped
          : outcome === 'not_run' || outcome === 'queued'
            ? styles.outcomeNotRun
            : styles.outcomePending;

  return <span className={classNames(styles.outcomePill, tone)}>{label}</span>;
};

// The record a cause points at. The carrier class and id say which row fired; this says which well,
// unit, collection, scenario or type curve version a reader should open to see the change itself.
const TriggerSubjectCell: FC<{ source: TriggerSource }> = ({ source }) => {
  const { kind, label, href } = source.subject ?? {
    kind: null,
    label: null,
    href: null,
  };
  if (!label) return <span className={styles.runFactMuted}>{EM_DASH}</span>;

  return (
    <span className={styles.causeSubject}>
      {href ? <PlainLink href={href}>{label}</PlainLink> : label}
      {kind && <span className={styles.causeSubjectKind}>{kind}</span>}
    </span>
  );
};

// What asked for this run. Several changes merge into one row, so this is a list rather than a
// field: triggeredBy keeps only the last carrier to arrive, which is why it cannot stand alone.
//
// origin is the half that was invisible before. An ingested change banks and waits for the drain; a
// person's edit resolves in its own callback and used to leave no record at all.
const TriggerCauses: FC<{
  sources: TriggerSource[];
  triggeredBy: string | null;
}> = ({ sources, triggeredBy }) => (
  <section className={styles.detailSection}>
    <h4 className={styles.sectionHeading}>What triggered this</h4>
    {sources.length === 0 ? (
      <p className={styles.runNote}>
        Nothing recorded
        {triggeredBy ? `. The last trigger was ${triggeredBy}` : ''}. Runs from
        before provenance was tracked have no causes.
      </p>
    ) : (
      <table className={styles.detailTable}>
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col">What changed</th>
            <th scope="col">Record</th>
            <th scope="col">When</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((source) => (
            <tr key={source.id}>
              <td>
                <span
                  className={classNames(
                    styles.originPill,
                    source.origin === 'manual'
                      ? styles.originManual
                      : styles.originIngested
                  )}
                >
                  {source.origin === 'manual' ? 'Manual edit' : 'Ingested'}
                </span>
              </td>
              <td>
                <TriggerSubjectCell source={source} />
              </td>
              <td className={styles.causeRecord}>
                {source.carrierType} #{source.carrierId}
              </td>
              <td className={styles.detailWhen}>
                {source.occurredAt
                  ? new Date(source.occurredAt).toLocaleString()
                  : EM_DASH}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </section>
);

// Per-unit outcomes. An area-wide run names no units up front, so its list arrives only once the
// generation request has answered.
const UnitOutcomes: FC<{ run: InventoryAutomationStatus }> = ({ run }) => {
  const { units, allUnits, systemWorkspaceHref } = run;

  return (
    <section className={styles.detailSection}>
      <h4 className={styles.sectionHeading}>Per unit</h4>
      {allUnits && !units?.length ? (
        <p className={styles.runNote}>
          This run covers every unit holding inventory in the area rather than a
          named list. The units appear here once it runs, from the generation
          request that records them
          {systemWorkspaceHref ? ', in the workspace linked on the row.' : '.'}
        </p>
      ) : units && units.length > 0 ? (
        <table className={styles.detailTable}>
          <thead>
            <tr>
              <th scope="col">Unit</th>
              <th scope="col">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => (
              <tr key={unit.id}>
                <td>
                  {unit.href ? (
                    <PlainLink href={unit.href} className={styles.unitLink}>
                      {unit.name ?? `#${unit.id}`}
                    </PlainLink>
                  ) : (
                    (unit.name ?? `#${unit.id}`)
                  )}
                </td>
                <td>
                  <OutcomePill outcome={unit.outcome} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={styles.runNote}>This run names no units.</p>
      )}
    </section>
  );
};

export const RunDetail: FC<{ run: InventoryAutomationStatus }> = ({ run }) => {
  const { outcomeCounts, formationNames, allFormations, allUnits } = run;

  return (
    <div className={styles.runDetail}>
      <dl className={styles.runFacts}>
        <div>
          <dt>Formations targeted</dt>
          <dd>
            {allFormations
              ? 'All formations in the area'
              : (formationNames ?? []).join(', ') || EM_DASH}
          </dd>
        </div>
        <div>
          <dt>Units targeted</dt>
          <dd>
            {allUnits
              ? 'Every unit holding inventory in the area'
              : `${run.unitCount ?? 0} named`}
          </dd>
        </div>
        <div>
          <dt>Outcomes</dt>
          <dd>
            {outcomeCounts
              ? `${outcomeCounts.completed} rebuilt · ${outcomeCounts.skipped} skipped · ${outcomeCounts.failed} failed`
              : EM_DASH}
            {outcomeCounts && outcomeCounts.queued > 0 && (
              <span className={styles.runFactMuted}>
                {' '}
                · {outcomeCounts.queued} queued
              </span>
            )}
            {outcomeCounts && outcomeCounts.inProgress > 0 && (
              <span className={styles.runFactMuted}>
                {' '}
                · {outcomeCounts.inProgress} in progress
              </span>
            )}
            {outcomeCounts && outcomeCounts.notRun > 0 && (
              <span className={styles.runFactMuted}>
                {' '}
                · {outcomeCounts.notRun} not run
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Attempts</dt>
          <dd>
            {run.attempts} of {run.maxAttempts}
            {run.retryable && (
              <span className={styles.willRetry}> · will retry</span>
            )}
          </dd>
        </div>
        {run.message && (
          <div className={styles.runFactWide}>
            <dt>Message</dt>
            <dd>{run.message}</dd>
          </div>
        )}
      </dl>

      <div className={styles.detailSections}>
        <TriggerCauses
          sources={run.triggerSources}
          triggeredBy={run.triggeredBy}
        />
        <UnitOutcomes run={run} />
      </div>
    </div>
  );
};
